<template>
  <!-- SPEC-ECOUNT-ETL-CRON-CONSOLE-001 Phase β — GTIN + Serial-Lot 스케줄 토글 패널 -->
  <a-card class="sync-toggle-panel" style="margin-bottom: 24px;">
    <template #title>
      <span>{{ $t('ecountEtl_syncConfig_title') }}</span>
      <!-- masterEnabled=false 시 inline 경고 -->
      <a-tag
        v-if="!state.masterEnabled"
        color="warning"
        style="margin-left: 12px; font-weight: normal;"
      >
        {{ $t('ecountEtl_syncConfig_masterOff_badge') }}
      </a-tag>
    </template>
    <template #extra>
      <a-button size="small" :loading="loading" @click="$emit('refresh')">
        {{ $t('ecountEtl_common_forceRefresh') }}
      </a-button>
    </template>

    <!-- masterEnabled=false 경고 배너 -->
    <a-alert
      v-if="!state.masterEnabled"
      type="warning"
      :message="$t('ecountEtl_syncConfig_masterOff_banner')"
      style="margin-bottom: 16px;"
      show-icon
    />

    <!-- 에러 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="$emit('clear-error')"
    />

    <a-row :gutter="24">
      <!-- GTIN 동기화 -->
      <a-col :xs="24" :md="12">
        <a-card size="small" class="sync-sub-card">
          <template #title>
            <div style="display: flex; align-items: center; gap: 8px;">
              <a-tooltip :title="state.masterEnabled ? '' : $t('ecountEtl_syncConfig_masterOff_tooltip')">
                <a-switch
                  :checked="state.gtinEnabled"
                  :disabled="!state.masterEnabled || loading"
                  :loading="patchingGtin"
                  @change="(val) => onToggle('gtin', val)"
                />
              </a-tooltip>
              <span>{{ $t('ecountEtl_syncConfig_gtin_title') }}</span>
              <a-tag :color="state.gtinEnabled ? 'green' : 'default'" style="margin-left: 4px;">
                {{ state.gtinEnabled ? $t('ecountEtl_syncConfig_status_active') : $t('ecountEtl_syncConfig_status_inactive') }}
              </a-tag>
            </div>
          </template>

          <a-descriptions :column="1" size="small">
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_schedule')">
              {{ state.gtinCronExpr ? cronToLabel(state.gtinCronExpr) : '—' }}
              <a-tag v-if="state.gtinCronExpr" color="default" size="small" style="margin-left: 6px; font-size: 11px;">
                {{ state.gtinCronExpr }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_lastSync')">
              <!-- D-17: lastSyncAt=null → '아직 가동되지 않음' -->
              <template v-if="state.gtinLastSyncAt">
                {{ formatTime(state.gtinLastSyncAt) }}
              </template>
              <a-typography-text v-else type="secondary">
                {{ $t('ecountEtl_syncConfig_notYetRun') }}
              </a-typography-text>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_nextSync')">
              <!-- D-17: nextSyncAt=null (마스터 OFF) → '마스터 OFF' -->
              <template v-if="state.gtinNextSyncAt">
                {{ formatTime(state.gtinNextSyncAt) }}
              </template>
              <a-typography-text v-else type="secondary">
                {{ state.masterEnabled ? '—' : $t('ecountEtl_syncConfig_masterOff_next') }}
              </a-typography-text>
            </a-descriptions-item>
          </a-descriptions>

          <a-button
            type="primary"
            size="small"
            :disabled="!state.masterEnabled || triggerLoadingGtin"
            :loading="triggerLoadingGtin"
            style="margin-top: 12px;"
            @click="onRunGtin"
          >
            {{ $t('ecountEtl_syncConfig_gtin_runNow') }}
          </a-button>
        </a-card>
      </a-col>

      <!-- Serial-Lot 동기화 -->
      <a-col :xs="24" :md="12">
        <a-card size="small" class="sync-sub-card">
          <template #title>
            <div style="display: flex; align-items: center; gap: 8px;">
              <a-tooltip :title="state.masterEnabled ? '' : $t('ecountEtl_syncConfig_masterOff_tooltip')">
                <a-switch
                  :checked="state.serialLotEnabled"
                  :disabled="!state.masterEnabled || loading"
                  :loading="patchingSerial"
                  @change="(val) => onToggle('serialLot', val)"
                />
              </a-tooltip>
              <span>{{ $t('ecountEtl_syncConfig_serialLot_title') }}</span>
              <a-tag :color="state.serialLotEnabled ? 'green' : 'default'" style="margin-left: 4px;">
                {{ state.serialLotEnabled ? $t('ecountEtl_syncConfig_status_active') : $t('ecountEtl_syncConfig_status_inactive') }}
              </a-tag>
            </div>
          </template>

          <a-descriptions :column="1" size="small">
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_schedule')">
              {{ state.serialLotCronExpr ? cronToLabel(state.serialLotCronExpr) : '—' }}
              <a-tag v-if="state.serialLotCronExpr" color="default" size="small" style="margin-left: 6px; font-size: 11px;">
                {{ state.serialLotCronExpr }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_lastSync')">
              <template v-if="state.serialLotLastSyncAt">
                {{ formatTime(state.serialLotLastSyncAt) }}
              </template>
              <a-typography-text v-else type="secondary">
                {{ $t('ecountEtl_syncConfig_notYetRun') }}
              </a-typography-text>
            </a-descriptions-item>
            <a-descriptions-item :label="$t('ecountEtl_syncConfig_nextSync')">
              <template v-if="state.serialLotNextSyncAt">
                {{ formatTime(state.serialLotNextSyncAt) }}
              </template>
              <a-typography-text v-else type="secondary">
                {{ state.masterEnabled ? '—' : $t('ecountEtl_syncConfig_masterOff_next') }}
              </a-typography-text>
            </a-descriptions-item>
          </a-descriptions>

          <a-button
            type="primary"
            size="small"
            :disabled="!state.masterEnabled || triggerLoadingSerial"
            :loading="triggerLoadingSerial"
            style="margin-top: 12px;"
            @click="onRunSerial"
          >
            {{ $t('ecountEtl_syncConfig_serialLot_runNow') }}
          </a-button>
        </a-card>
      </a-col>
    </a-row>
  </a-card>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-ETL-CRON-CONSOLE-001 Phase β AC-FE-001~007
// @MX:SPEC: SPEC-CHINA-ADMIN-LOCALE-PHASE1-001 영역 C REQ-LOCALE-P1-FE-020~030
// @MX:NOTE: [AUTO] studio crm v2 ConnectorList.vue status badge 패턴(● 활성/○ 비활성) 차용
// 토글 2개(GTIN + Serial-Lot) + 즉시 실행 2 버튼 + optimistic lock(409) + masterEnabled UX

import { ref } from 'vue';
import { useSyncConfig } from '../composables/useSyncConfig.js';
import { cronToLabel } from '../../../utils/ecountEtl/cronPresets.js';
import { useKstTime } from '../composables/useKstTime.js';
// REQ-LOCALE-P1-FE-007/NFR-008: lazy i18n — setup() 함수 본문 안에서만 t() 호출
import i18n from '@/i18n';

export default {
  name: 'SyncTogglePanel',

  emits: ['refresh', 'clear-error', 'toast'],

  setup(props, { emit }) {
    const { state, loading, error, triggerLoadingGtin, triggerLoadingSerial, fetch, patchConfig, runGtinSync, runSerialLotSync } = useSyncConfig();
    const { format } = useKstTime();

    // 토글별 PATCH 중 loading 상태 (UX-2: 토글 disabled + spinner)
    const patchingGtin = ref(false);
    const patchingSerial = ref(false);

    // REQ-LOCALE-P1-FE-020: setup() 내 i18n.global.t() 래퍼 (this 바인딩 불필요)
    function t(key, params) {
      return i18n.global.t(key, params);
    }

    /**
     * 토글 변경 → confirm dialog → PATCH
     * AC-FE-002: OFF 시 confirm dialog 표시
     * REQ-LOCALE-P1-FE-021: confirmDisable + jobLabel i18n
     */
    async function onToggle(type, value) {
      // OFF로 바꾸는 경우 confirm (REQ-LOCALE-P1-FE-021)
      if (!value) {
        const jobLabelKey = type === 'gtin'
          ? 'ecountEtl_toggle_jobLabel_gtin'
          : 'ecountEtl_toggle_jobLabel_serialLot';
        const jobLabel = t(jobLabelKey);
        const ok = window.confirm(t('ecountEtl_toggle_confirmDisable', { jobLabel }));
        if (!ok) return;
      }

      const patch = type === 'gtin'
        ? { gtinEnabled: value }
        : { serialLotEnabled: value };

      if (type === 'gtin') patchingGtin.value = true;
      else patchingSerial.value = true;

      try {
        const result = await patchConfig(patch);
        if (result.conflict) {
          // REQ-LOCALE-P1-FE-022: 409 conflict toast
          emit('toast', {
            type: 'warning',
            message: t('ecountEtl_toggle_conflictRefreshed'),
          });
        } else if (result.success) {
          // REQ-LOCALE-P1-FE-023: slackNotified=false warning toast
          if (!result.slackNotified) {
            emit('toast', {
              type: 'warning',
              message: t('ecountEtl_toggle_slackWarn'),
            });
          } else {
            // REQ-LOCALE-P1-FE-024: 저장 성공 toast
            emit('toast', { type: 'success', message: t('ecountEtl_toggle_saved') });
          }
        }
      } finally {
        patchingGtin.value = false;
        patchingSerial.value = false;
      }
    }

    /**
     * GTIN 즉시 실행
     * REQ-LOCALE-P1-FE-025~027: apiKey203 / runStarted / runFailed i18n
     */
    async function onRunGtin() {
      const result = await runGtinSync();
      if (result.loginFail) {
        // REQ-LOCALE-P1-FE-025: code=203 toast
        emit('toast', {
          type: 'error',
          message: t('ecountEtl_toggle_apiKey203'),
        });
      } else if (result.success) {
        // REQ-LOCALE-P1-FE-026: 실행 성공 toast (runId 보간, REQ-028: null → '—')
        emit('toast', {
          type: 'success',
          message: t('ecountEtl_toggle_runStarted', { job: 'GTIN', runId: result.runId ?? '—' }),
        });
      } else {
        // REQ-LOCALE-P1-FE-027: 실행 실패 toast
        emit('toast', { type: 'error', message: t('ecountEtl_toggle_runFailed', { job: 'GTIN' }) });
      }
    }

    /**
     * Serial-Lot 즉시 실행
     * REQ-LOCALE-P1-FE-025~027: 동일 패턴
     */
    async function onRunSerial() {
      const result = await runSerialLotSync();
      if (result.loginFail) {
        emit('toast', {
          type: 'error',
          message: t('ecountEtl_toggle_apiKey203'),
        });
      } else if (result.success) {
        emit('toast', {
          type: 'success',
          message: t('ecountEtl_toggle_runStarted', { job: 'Serial-Lot', runId: result.runId ?? '—' }),
        });
      } else {
        emit('toast', { type: 'error', message: t('ecountEtl_toggle_runFailed', { job: 'Serial-Lot' }) });
      }
    }

    function formatTime(value) {
      return format(value);
    }

    return {
      state,
      loading,
      error,
      patchingGtin,
      patchingSerial,
      triggerLoadingGtin,
      triggerLoadingSerial,
      fetch,
      cronToLabel,
      formatTime,
      onToggle,
      onRunGtin,
      onRunSerial,
    };
  },

  mounted() {
    this.fetch();
  },
};
</script>

<style scoped>
.sync-toggle-panel {
  border: 1px solid #f0f0f0;
}

.sync-sub-card {
  height: 100%;
  border: 1px solid #f5f5f5;
}
</style>
